import { ITranslationsCollection, ITranslationsCollections } from '@estee/elc-service';
import { BrowserStorage } from '@estee/elc-universal-utils';

import {
    socialPlaceholderRegex,
    StorageKey,
    translationPlaceholderRegex
} from '~constants/Constants';
import { translationKeyMap } from '~constants/TranslationKeyMap';

const translationSetNamespaces = [
    'elc_account',
    'elc_account_errors',
    'elc_user',
    'elc_general',
    'elc_errors',
    'elc_common',
    'elc_checkout',
    'elc_stores',
    'elc_communications'
];

const mapTranslations = (
    shortPathKeyTranslations: ITranslationsCollection,
    completePathKeyTranslations: ITranslationsCollection
) => {
    return Object.entries(translationKeyMap).reduce((translations, [reactKey, drupalKey]) => {
        translations[reactKey] =
            completePathKeyTranslations[drupalKey] ||
            shortPathKeyTranslations[drupalKey] ||
            drupalKey;

        return translations;
    }, {} as ITranslationsCollection);
};

const getRawTranslationMappings = (translationSets: ITranslationsCollections) => {
    // Cache it through the loop so we can later processing the mapping to "camelCase key"
    const shortPathKeyTranslations: ITranslationsCollection = {};

    // Translation collection uses fully qualified path as translation key, i.e. `{ 'elc_account.first_name': 'First Name' }`
    // side effect as caching Drupal translation key (without translation set name, i.e. `{ 'first_name': 'First Name' }`)
    const completePathKeyTranslations = Object.entries(translationSets).reduce(
        (translations, [translationSetName, translationSet]) => {
            Object.entries(translationSet).forEach(([drupalKey, translationValue]) => {
                translations[`${translationSetName}.${drupalKey}`] = translationValue;
                shortPathKeyTranslations[drupalKey] = translationValue;
            });

            return translations;
        },
        {} as ITranslationsCollection
    );

    return Object.assign(
        completePathKeyTranslations,
        mapTranslations(shortPathKeyTranslations, completePathKeyTranslations)
    );
};

const processPlaceholders = (
    originTranslationValue: string,
    placeholderKeys: string[],
    rawTranslations: ITranslationsCollection
) => {
    // `placeholderKey' is the camel case translation key for placeholder such as 'privacyPolicyLink'
    return placeholderKeys.reduce((updatedTranslationValue, placeholderKey) => {
        const placeholderValue = rawTranslations[placeholderKey];

        if (!placeholderValue) {
            return updatedTranslationValue;
        }

        return updatedTranslationValue.replace(
            new RegExp(`::${placeholderKey}::`, 'g'),
            placeholderValue.html ?? placeholderValue
        );
    }, originTranslationValue);
};

export const buildIamTranslations = (translationSets: ITranslationsCollections) => {
    // e.g. "Facebook" for social provider "facebook"
    const socialDisplayName = BrowserStorage.getItem(StorageKey.SOCIAL_DISPLAY_NAME) ?? '';

    const iamTranslationSets = translationSetNamespaces.reduce<ITranslationsCollections>(
        (sets, namespace) => {
            // ?. is for avoiding sentry issue if vulcan cannot loading translations correctly
            const translations = translationSets?.[namespace];
            if (translations) {
                sets[namespace] = translations;
            }

            return sets;
        },
        {}
    );

    // key is either camel case ones defined in `TranslationKeyMap`, or fully qualified key as `elc_account.first_name`
    const rawTranslations = getRawTranslationMappings(iamTranslationSets);

    return Object.entries(rawTranslations).reduce(
        (translations, [translationKey, translationValue]) => {
            let updatedTranslationValue = translationValue;

            // in rare cases we add HTML type translations in mappings, we won't handle placeholders for them
            if (typeof translationValue === 'string') {
                if (socialDisplayName) {
                    updatedTranslationValue = translationValue.replace(
                        socialPlaceholderRegex,
                        socialDisplayName
                    );
                }
                const matches = [...updatedTranslationValue.matchAll(translationPlaceholderRegex)];
                if (matches.length) {
                    updatedTranslationValue = processPlaceholders(
                        updatedTranslationValue,
                        matches.map(([, matchedKey]) => matchedKey),
                        rawTranslations
                    );
                }
            }
            translations[translationKey] = updatedTranslationValue || translationKey;

            return translations;
        },
        {} as ITranslationsCollection
    );
};
